import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import { getDatabase, ref, get, remove } from 'firebase/database';
import { Button, Alert, Modal, Collapse } from 'react-bootstrap';
import { Plus, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useIsSmallScreen } from '../../hooks/useIsSmallScreen';
import './BuildingCheckTemplatesEditor.css';
import {
    BuildingCheckTemplate,
    INTERVAL_UNITS,
    TEMPLATE_CATEGORIES,
    TemplateCategory,
    InspectionCategory
} from './types';
import BuildingCheckTemplateDetailPane from './BuildingCheckTemplateDetailPane';
import BuildingCheckTemplateModal from './BuildingCheckTemplateModal';
import { tombstoneAttachmentDiff } from '../../api/fileDeletionApi';
import { recordActivity } from '../../api/activityApi';

const BuildingCheckTemplatesEditor: React.FC = () => {
    const [templates, setTemplates] = useState<BuildingCheckTemplate[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [editingTemplate, setEditingTemplate] = useState<BuildingCheckTemplate | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [templateToDelete, setTemplateToDelete] = useState<BuildingCheckTemplate | null>(null);
    const [alert, setAlert] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);

    // Selection state
    const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

    // Collapsed sections state
    const [collapsedSections, setCollapsedSections] = useState<Set<TemplateCategory>>(new Set());

    const db = getDatabase();

    // Group templates by category
    const templatesByCategory = useMemo(() => {
        const grouped: Record<TemplateCategory, BuildingCheckTemplate[]> = {
            [InspectionCategory.MeterReadings]: [],
            [InspectionCategory.RoutineOperations]: [],
            [InspectionCategory.Service]: [],
            [InspectionCategory.ConditionAssessment]: []
        };

        templates.forEach(template => {
            const category = template.category || InspectionCategory.MeterReadings;
            if (grouped[category]) {
                grouped[category].push(template);
            } else {
                grouped[InspectionCategory.MeterReadings].push(template);
            }
        });

        // Sort by name within each category
        Object.keys(grouped).forEach(cat => {
            grouped[cat as TemplateCategory].sort((a, b) => a.name.localeCompare(b.name, 'sv'));
        });

        return grouped;
    }, [templates]);

    // Get selected template
    const selectedTemplate = useMemo(() => {
        return templates.find(t => t.id === selectedTemplateId) || null;
    }, [templates, selectedTemplateId]);

    // Phones show either the list or the open template, never both. Going back
    // returns the list to where it was scrolled.
    const isSmallScreen = useIsSmallScreen();
    const mobileView = isSmallScreen && selectedTemplate ? 'detail' : 'list';
    const wrapperRef = useRef<HTMLDivElement>(null);
    const listScrollTopRef = useRef(0);

    useLayoutEffect(() => {
        const scroller = wrapperRef.current?.closest('.main-content');
        if (!scroller) return;
        scroller.scrollTop = mobileView === 'list' ? listScrollTopRef.current : 0;
    }, [mobileView]);

    const openTemplate = (templateId: string) => {
        listScrollTopRef.current = wrapperRef.current?.closest('.main-content')?.scrollTop ?? 0;
        setSelectedTemplateId(templateId);
    };

    // Toggle section collapse
    const toggleSection = useCallback((category: TemplateCategory) => {
        setCollapsedSections(prev => {
            const next = new Set(prev);
            if (next.has(category)) {
                next.delete(category);
            } else {
                next.add(category);
            }
            return next;
        });
    }, []);

    // Load templates from Firebase
    const loadTemplates = async (): Promise<void> => {
        try {
            setLoading(true);
            const templatesRef = ref(db, 'inspections/templates');
            const snapshot = await get(templatesRef);

            if (snapshot.exists()) {
                const data = snapshot.val() as Record<string, BuildingCheckTemplate>;
                const templatesArray = Object.entries(data).map(([id, template]) => ({
                    ...template,
                    id
                }));
                setTemplates(templatesArray);
            } else {
                setTemplates([]);
            }
        } catch (error) {
            console.error('Error loading templates:', error);
            setAlert({ type: 'danger', message: 'Fel vid laddning av mallar' });
        } finally {
            setLoading(false);
        }
    };

    // Delete template
    const deleteTemplate = async (template: BuildingCheckTemplate): Promise<void> => {
        try {
            setSaving(true);
            const templateRef = ref(db, `inspections/templates/${template.id}`);
            await remove(templateRef);
            recordActivity('settings.updated', { targetKey: template.id, label: template.name });

            // Soft-delete the deleted template's attachment blobs
            await tombstoneAttachmentDiff(
                'inspectionTemplateAttachment',
                template.instructionalAttachments,
                undefined
            );

            setShowDeleteModal(false);
            setTemplateToDelete(null);
            if (selectedTemplateId === template.id) {
                setSelectedTemplateId(null);
            }
            loadTemplates();
        } catch (error) {
            console.error('Error deleting template:', error);
            setAlert({ type: 'danger', message: 'Fel vid borttagning av mall' });
        } finally {
            setSaving(false);
        }
    };

    // Open modal for new template
    const openNewTemplateModal = (): void => {
        setEditingTemplate(null);
        setShowModal(true);
    };

    // Open modal for editing template
    const openEditTemplateModal = (template: BuildingCheckTemplate): void => {
        setEditingTemplate(template);
        setShowModal(true);
    };

    const handleTemplateSaved = async (template: BuildingCheckTemplate): Promise<void> => {
        setShowModal(false);
        setEditingTemplate(null);
        await loadTemplates();
        setSelectedTemplateId(template.id);
    };

    // Handle delete from detail pane
    const handleDeleteFromDetailPane = (template: BuildingCheckTemplate) => {
        if (template.system) {
            setAlert({ type: 'danger', message: 'Systemmallar för skickbedömning kan inte tas bort' });
            return;
        }
        setTemplateToDelete(template);
        setShowDeleteModal(true);
    };

    useEffect(() => {
        loadTemplates();
    }, []);

    if (loading) {
        return (
            <div className="building-checks-page-wrapper">
                <div className="d-flex align-items-center justify-content-center h-100">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Laddar...</span>
                    </div>
                </div>
            </div>
        );
    }

    const categoryOrder: TemplateCategory[] = [
        InspectionCategory.MeterReadings,
        InspectionCategory.RoutineOperations,
        InspectionCategory.Service,
        InspectionCategory.ConditionAssessment
    ];

    return (
        <div className="building-checks-page-wrapper" ref={wrapperRef} data-mobile-view={mobileView}>
            {alert && (
                <Alert variant={alert.type} dismissible onClose={() => setAlert(null)} className="mb-3">
                    {alert.message}
                </Alert>
            )}

            <div className="building-checks-page std-box">
                {/* Left Pane - Template List */}
                <div className="building-checks-left-pane">
                    <div className="left-pane-header">
                        <Button variant="primary" size="sm" onClick={openNewTemplateModal} style={{ backgroundColor: '#001D60', borderColor: '#001D60' }}>
                            <Plus size={16} className="me-1" />
                            Skapa ny
                        </Button>
                    </div>

                    {templates.some(t => t.regulatoryType) && (
                        <div className="template-list-legend">
                            <span className="template-item-indicator myndighetskrav" />
                            <span className="template-list-legend-label">Myndighetskrav</span>
                        </div>
                    )}
                    <div className="template-list-container">
                        {templates.length === 0 ? (
                            <div className="empty-template-list">
                                <p>Inga mallar än</p>
                                <p className="text-muted small">Skapa din första mall för att komma igång.</p>
                            </div>
                        ) : (
                            categoryOrder.map(category => {
                                const templatesInCategory = templatesByCategory[category];
                                const count = templatesInCategory.length;
                                const isCollapsed = collapsedSections.has(category);
                                const categoryLabel = TEMPLATE_CATEGORIES.find(c => c.value === category)?.label || category;

                                if (count === 0) return null;

                                return (
                                    <div key={category} className="template-category-section">
                                        <div
                                            className="template-category-header"
                                            onClick={() => toggleSection(category)}
                                            role="button"
                                            aria-expanded={!isCollapsed}
                                        >
                                            {isCollapsed ? (
                                                <ChevronRight size={16} className="template-category-chevron" />
                                            ) : (
                                                <ChevronDown size={16} className="template-category-chevron" />
                                            )}
                                            <span className="template-category-name">{categoryLabel}</span>
                                            <span className="badge bg-secondary">{count}</span>
                                        </div>

                                        <Collapse in={!isCollapsed}>
                                            <div className="template-list">
                                                {templatesInCategory.map(template => (
                                                    <div
                                                        key={template.id}
                                                        className={`template-item ${selectedTemplateId === template.id ? 'selected' : ''}`}
                                                        onClick={() => openTemplate(template.id)}
                                                    >
                                                        <div className="template-item-info">
                                                            <div className="template-item-name">{template.name}</div>
                                                            <div className="template-item-meta">
                                                                Var {template.interval.value} {INTERVAL_UNITS.find(u => u.value === template.interval.unit)?.label.toLowerCase()}
                                                            </div>
                                                        </div>
                                                        {template.regulatoryType && (
                                                            <span className="template-item-indicator myndighetskrav" title="Myndighetskrav" />
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </Collapse>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* Right Pane - Detail */}
                <div className="building-checks-right-pane">
                    {mobileView === 'detail' && (
                        <button type="button" className="building-checks-back-button" onClick={() => setSelectedTemplateId(null)}>
                            <ChevronLeft size={20} aria-hidden="true" />
                            Alla mallar
                        </button>
                    )}
                    {selectedTemplate ? (
                        <BuildingCheckTemplateDetailPane
                            template={selectedTemplate}
                            onEdit={openEditTemplateModal}
                            onDelete={handleDeleteFromDetailPane}
                        />
                    ) : (
                        <div className="empty-state">
                            <div className="text-muted">
                                Välj en mall från listan för att visa detaljer
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Template Modal */}
            {showModal && (
                <BuildingCheckTemplateModal
                    template={editingTemplate}
                    onSaved={handleTemplateSaved}
                    onCancel={() => setShowModal(false)}
                />
            )}

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} dialogClassName="building-checks-editor-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Bekräfta borttagning</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Är du säker på att du vill ta bort mallen <strong>{templateToDelete?.name}</strong>?</p>
                    <p className="text-muted">Denna åtgärd kan inte ångras.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Avbryt
                    </Button>
                    <Button
                        variant="danger"
                        onClick={() => templateToDelete && deleteTemplate(templateToDelete)}
                        disabled={saving}
                    >
                        {saving ? 'Tar bort...' : 'Ta bort'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default BuildingCheckTemplatesEditor;
